import axios from 'axios';
import constants from '../../constants';
import { cookieStorageUtility } from '../../utils/utilityFunctions';

const { serverUrl, ENCRYPTOR_KEY } = constants;

const authToken = () => {
  const authUserId = cookieStorageUtility.fetch('auth_user_id');
  const encryptedToken = cookieStorageUtility.fetch('encrypted_' + ENCRYPTOR_KEY + '_token');
  return btoa(authUserId + '|' + encryptedToken);
};

// Normalises the error shapes RedChannel's Airbnb API returns:
//   blocked step  → { status: 'blocked', reasons: [] }            (422)
//   NextPax error → { status: 'error', code, message, request_id, invalid_fields }
//   validation    → { code, message }                            (422/404/400)
export class AirbnbApiError extends Error {
  constructor(status, body) {
    const data = body || {};
    super(data.message || (data.reasons ? 'Step is blocked' : `Request failed (${status})`));
    this.status = status;
    this.blocked = data.status === 'blocked';
    this.reasons = data.reasons || [];
    this.code = typeof data.code === 'string' ? data.code : null;
    this.requestId = data.request_id || null;
    this.invalidFields = data.invalid_fields || null;
  }
}

const request = (method, path, { params, data } = {}) =>
  axios(`${serverUrl}airbnb/${path}`, {
    method,
    params,
    data,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      token: authToken()
    },
    validateStatus: () => true
  }).then(response => {
    // RedChannel's check_token answers 200 with { status: 401 } on a bad token
    const body = response.data;
    if (response.status >= 400 || (body && body.status === 401)) {
      throw new AirbnbApiError(response.status >= 400 ? response.status : 401, body);
    }
    return body;
  });

const airbnbApi = {
  // Property managers
  listPropertyManagers: () => request('GET', 'property_managers'),
  getPropertyManager: id => request('GET', `property_managers/${id}`),
  createPropertyManager: data => request('POST', 'property_managers', { data }),
  updatePropertyManager: (id, data) => request('PATCH', `property_managers/${id}`, { data }),

  // Properties
  listProperties: pmId => request('GET', `property_managers/${pmId}/properties`),
  // Live NextPax property index (paginated with limit/offset)
  nextpaxProperties: (pmId, params) => request('GET', `property_managers/${pmId}/nextpax_properties`, { params }),
  createProperty: (pmId, data) => request('POST', `property_managers/${pmId}/properties`, { data }),
  getProperty: id => request('GET', `properties/${id}`),

  // Multi-unit: the hotel's room categories, and the unit types built from them
  roomCategories: id => request('GET', `properties/${id}/room_categories`),
  listUnitTypes: id => request('GET', `properties/${id}/unit_types`),
  createUnitTypes: (id, roomCategoryIds, typeCode) =>
    request('POST', `properties/${id}/unit_types`, { data: { room_category_ids: roomCategoryIds, type_code: typeCode } }),
  bulkRunUnitTypes: (id, step) => request('POST', `properties/${id}/unit_types/flow_a/${step}/bulk_run`),
  updateProperty: (id, data) => request('PATCH', `properties/${id}`, { data }),
  // Switch Airbnb off (room types first for a multi-unit) / remove from NextPax and RedChannel
  disableAirbnb: id => request('POST', `properties/${id}/disable_airbnb`),
  deleteProperty: id => request('DELETE', `properties/${id}`),

  // Images / cancellation policies (stored on the property's gh_detail)
  listImages: propertyId => request('GET', `properties/${propertyId}/images`),
  createImage: (propertyId, data) => request('POST', `properties/${propertyId}/images`, { data }),
  deleteImage: (propertyId, id) => request('DELETE', `properties/${propertyId}/images/${id}`),
  updateImage: (propertyId, id, data) => request('PATCH', `properties/${propertyId}/images/${id}`, { data }),
  // Import the hotel's photos from the RedDoorz sync_photos API
  syncPhotos: propertyId => request('POST', `properties/${propertyId}/images/sync`),
  listPolicies: propertyId => request('GET', `properties/${propertyId}/cancellation_policies`),
  createPolicy: (propertyId, data) => request('POST', `properties/${propertyId}/cancellation_policies`, { data }),
  updatePolicy: (propertyId, id, data) =>
    request('PATCH', `properties/${propertyId}/cancellation_policies/${id}`, { data }),
  deletePolicy: (propertyId, id) => request('DELETE', `properties/${propertyId}/cancellation_policies/${id}`),

  // Flow A — subjectPath is `property_managers/:id` or `properties/:id`
  flowSteps: subjectPath => request('GET', `${subjectPath}/flow_a`),
  runStep: (subjectPath, step) => request('POST', `${subjectPath}/flow_a/${step}`),
  bulkRunStep: (pmId, step) => request('POST', `property_managers/${pmId}/flow_a/${step}/bulk_run`),

  // Master data (NextPax code lists)
  masterDataCategories: () => request('GET', 'master_data/categories'),
  masterData: params => request('GET', 'master_data', { params }),
  syncMasterData: () => request('POST', 'master_data/sync'),

  // NextPax Notifications API (booking webhooks)
  notificationSubscriptions: () => request('GET', 'notification_subscriptions'),
  subscribeNotifications: notificationType =>
    request('POST', 'notification_subscriptions', { data: { notification_type: notificationType } }),
  unsubscribeNotifications: notificationType => request('DELETE', `notification_subscriptions/${notificationType}`),
  notificationEvents: params => request('GET', 'notification_events', { params })
};

export default airbnbApi;
